/* Los repuestos que se le colocaron a una unidad en un ticket del taller.
 *
 * Igna, 22/10: la CantApp vieja se retira, y lo que el taller cargaba ahí y la
 * ticketera no tenía es QUÉ PIEZAS SE LE PUSIERON AL CAMIÓN. Cada renglón dice
 * qué (del stock del pañol, o escrito a mano), cuánto, y SIEMPRE quién lo
 * cargó y cuándo — eso lo pone la base desde la sesión, no esta pantalla
 * (`20261022230000_ticketera_repuestos_colocados.sql`).
 *
 * Acá vive la lógica sin React: qué se puede mandar, cómo se dice quién y
 * cuándo, y quién puede anular. La pantalla sólo dibuja.
 */

const texto = (v) => String(v ?? "").trim();

/* «12,5» y «12.5» son lo mismo para el que escribe con el teléfono. */
export function numero(v) {
  const s = texto(v).replace(/\s/g, "").replace(",", ".");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

/* Los km se escriben con puntos de miles («412.000») y sin decimales. */
export function kilometros(v) {
  const s = texto(v).replace(/[.\s]/g, "");
  if (!s) return null;
  if (!/^\d+$/.test(s)) return NaN;
  return Number(s);
}

/* Lo que va a la base, o el motivo por el que no se manda. La base vuelve a
 * mirar todo; esto es para no hacer esperar a nadie por un error que se ve
 * desde acá. */
export function validarNuevo({ descripcion, articulo, cantidad, km } = {}) {
  if (!texto(descripcion) && !articulo) {
    return { error: "Decí qué repuesto se colocó, o buscalo en el stock." };
  }
  const c = numero(cantidad === undefined ? "1" : cantidad);
  if (c === null || Number.isNaN(c) || !(c > 0)) {
    return { error: "La cantidad tiene que ser mayor a cero." };
  }
  const k = kilometros(km);
  if (Number.isNaN(k) || (k !== null && k >= 10000000)) {
    return { error: "Los km tienen que ser un número de odómetro (o dejalos vacíos)." };
  }
  return { error: "", cantidad: c, km: k };
}

export const MOMENTO = {
  durante: "durante el trabajo",
  despues: "después de cerrado",
};

/* dd/mm/aaaa hh:mm en hora argentina (UTC−3 fijo), igual que la ticketera:
 * el formato no puede depender del navegador de quien mira. Lleva el año
 * porque esto se lee meses después. */
const dos = (n) => String(n).padStart(2, "0");
export function fechaHora(iso) {
  if (!iso) return "";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  const d = new Date(t - 3 * 3600 * 1000);
  return `${dos(d.getUTCDate())}/${dos(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${dos(d.getUTCHours())}:${dos(d.getUTCMinutes())}`;
}

/* «Cargó Pablo Garibay · 22/10/2026 14:05 · durante el trabajo». */
export function quienLoCargo(r) {
  if (!r) return "";
  const partes = [`Cargó ${r.cargado_nombre || r.cargado_por || "—"}`];
  if (r.cargado_en) partes.push(fechaHora(r.cargado_en));
  if (MOMENTO[r.momento]) partes.push(MOMENTO[r.momento]);
  return partes.join(" · ");
}

export function quienLoAnulo(r) {
  if (!r || !r.anulado_en) return "";
  return `Anuló ${r.anulado_nombre || r.anulado_por || "—"} · ${fechaHora(r.anulado_en)}`
    + (r.anulado_motivo ? ` · ${r.anulado_motivo}` : "");
}

/* Anular es SÓLO de quien lo cargó, y una vez. Esconder el botón no es la
 * regla —la base la hace cumplir— pero ofrecérselo a otro sería ofrecerle un
 * botón que le va a contestar que no. */
export function puedeAnular(r, miMail, puedeCargar) {
  if (!r || r.anulado_en || !puedeCargar) return false;
  const yo = texto(miMail).toLowerCase();
  return !!yo && texto(r.cargado_por).toLowerCase() === yo;
}

/* Cantidad sin ceros de más, con la unidad si no es la obvia:
 * «4», «6 METROS». «UNIDADES» se calla: es lo que se espera. */
export function cuanto(cantidad, unidad) {
  const n = Number(cantidad);
  const c = Number.isFinite(n)
    ? (Number.isInteger(n) ? String(n) : String(n).replace(".", ","))
    : texto(cantidad);
  const u = texto(unidad);
  if (!u || /^unidad(es)?$/i.test(u)) return c;
  return `${c} ${u}`;
}

/* «412.000 km», a mano y no con `toLocaleString`: el Node de los tests y cada
 * navegador ponen los separadores distinto. Vacío cuando no hay dato, que no es
 * un cero. */
export function kmTexto(v) {
  if (v === null || v === undefined || texto(v) === "") return "";
  const n = Number(String(v).replace(/[.\s]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return "";
  return `${String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".")} km`;
}

/* Lo vigente primero en el orden en que se cargó; lo anulado queda abajo,
 * a la vista pero apartado. */
export function ordenar(renglones) {
  const lista = [...(renglones || [])].filter(Boolean);
  return lista.sort((a, b) => {
    const an = a.anulado_en ? 1 : 0;
    const bn = b.anulado_en ? 1 : 0;
    if (an !== bn) return an - bn;
    return Number(a.id) - Number(b.id);
  });
}

/* ── LA FICHA DE LA UNIDAD: los de CantApp y los de la ticketera ─────────
 *
 * `repuestos_unidad()` ya los devuelve ordenados; se vuelven a ordenar acá
 * igual (estable, por fecha descendente) porque el bloque no puede depender
 * de que la base no cambie de idea, y una fila sin fecha va al final en vez
 * de arriba de todo. */
export const ORIGEN = {
  cantapp: "CantApp",
  ticketera: "Ticketera",
};

export function repuestosDeUnidad(filas) {
  const lista = (filas || []).filter(Boolean).map((f, i) => {
    const t = Date.parse(f.cuando);
    return {
      ...f,
      i,
      t: Number.isNaN(t) ? null : t,
      clave: `${f.origen}-${f.renglon ?? i}`,
      origenTexto: ORIGEN[f.origen] || f.origen,
      /* Sólo un ticket tiene a dónde ir; el evento de CantApp no tiene
       * pantalla (los talleres viejos salieron del hub). */
      enlace: f.origen === "ticketera" && /^\d+$/.test(String(f.origen_id ?? ""))
        ? `/taller/ticket/${f.origen_id}`
        : null,
    };
  });
  lista.sort((a, b) => {
    if (a.t === null && b.t === null) return a.i - b.i;
    if (a.t === null) return 1;
    if (b.t === null) return -1;
    return b.t - a.t || a.i - b.i;
  });
  return lista;
}

/* «Cargó Pirelli, Braian · colocó FROIMAN, JAVIER». Si son la misma persona
 * se dice una vez. */
export function quienes(f) {
  const cargo = texto(f?.cargado_por);
  const coloco = texto(f?.colocado_por);
  if (cargo && coloco && cargo.toLowerCase() === coloco.toLowerCase()) return `Cargó y colocó ${cargo}`;
  return [cargo && `Cargó ${cargo}`, coloco && `colocó ${coloco}`].filter(Boolean).join(" · ");
}
